/**
 * A read-only tap on the analytics pipeline, for verifying that events actually fire.
 *
 * The console is not a usable answer everywhere: the `dev` EAS profile builds Release with no
 * development client, so `__DEV__` is false and there is no Metro session or device console to read.
 * Publishing each event here lets a host render them instead — the mobile debug overlay subscribes,
 * and the webapp forwards WebView events into the same overlay over the bridge.
 *
 * Module scope on purpose: the publisher is a hook used in dozens of places and the subscriber is a
 * single overlay mounted at the root, so threading a context between them would buy nothing.
 *
 * Nothing here runs unless a platform provider sets `verboseLogging`, which is bound to the DEV
 * environment in both apps.
 */
export interface AnalyticsDebugEvent {
  /** ANALYTICS_EVENT_TYPE value — `custom` for everything going through `trackCustom`. */
  eventType: string;
  /** Canonical name for custom events; absent on typed ones (view, add_to_cart, …). */
  eventName?: string;
  params?: Record<string, unknown>;
  /** `Web` when the event was raised inside the app's WebView rather than natively. */
  platform: string;
  /** Epoch ms, stamped by the publisher so a forwarded event keeps its original time. */
  occurredAt: number;
}

type AnalyticsDebugListener = (event: AnalyticsDebugEvent) => void;

const listeners = new Set<AnalyticsDebugListener>();

/** Returns its own unsubscribe, so a `useEffect` can hand it straight back. */
export const subscribeToAnalyticsDebug = (listener: AnalyticsDebugListener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const publishAnalyticsDebugEvent = (event: AnalyticsDebugEvent): void => {
  listeners.forEach((listener) => {
    try {
      listener(event);
    } catch {
      // A debug surface must never be able to break the event it is only watching.
    }
  });
};

/** Test seam: drops subscribers left behind by a previous case. */
export const resetAnalyticsDebugSubscribers = (): void => {
  listeners.clear();
};
